import React from "react";
import { PropError, PropLabel, PropNote } from "../PropX";
import { LooseTextInput } from "../__Internal__";
import { useHTMLInputElementValidationProjection } from "../hooks/useHTMLInputElementValidationProjection";
import { LoadingState } from "../states/LoadingState";
import { forkedRef } from "../utils/forkedRef";

/**
 * These are keys internal to `LooseTextInput` that we don't need to expose to the developer.
 */
type InternalKeys = "type" | "aria-hidden" | "onValid" | "loading" | "invalid" | "rows" | `UNSAFE_${string}`;

/**
 * Keys that only apply to the `Number` or `Email`.
 */
type StandardOmittedKeys = "min" | "max" | "step" | "multiple";

/**
 * Keys that only apply to the `Number`.
 */
type EmailOmittedKeys = "min" | "max" | "step";

/**
 * These are the props that are common to all input types _except_ for `Number`.
 */
type NumberOmittedKeys = "autoComplete" | "spellCheck" | "minLength" | "maxLength" | "pattern" | "multiple";

type SharedPublicProps = {
  /**
   * A label for the input.
   * @default undefined
   */
  label?: PropLabel;

  /**
   * A note to display under the input. Hidden when there is an error.
   * @default undefined
   */
  note?: PropNote;

  /**
   * A custom error to display for the input. Overrides standard errors created by `required` or `pattern`.
   * @default undefined
   */
  error?: PropError | PropError[];

  /**
   * The event for which to validate the input.
   * @default "blur"
   */
  validateOn?: "change" | "blur" | "manual";
};

type LooseTextInputProps = React.ComponentProps<typeof LooseTextInput>;
type FactoryProps = Omit<LooseTextInputProps, "type"> & SharedPublicProps;

const inputFactory = <Type extends LooseTextInputProps["type"]>(type: Type, more: Partial<LooseTextInputProps>) => {
  type RefType = Type extends "textarea" ? HTMLTextAreaElement : HTMLInputElement;

  return (props: FactoryProps, forwardedRef: React.ForwardedRef<RefType>) => {
    const { label, note, name, required = false, disabled = false, validateOn = "blur", ...rest } = props;
    const ref = React.useRef<HTMLTextAreaElement | HTMLInputElement>(null);

    const [errors, propsForInput] = useHTMLInputElementValidationProjection(props, { ref, validateOn });
    const { invalid } = propsForInput;

    const prefix = (() => {
      if (label) {
        return PropLabel.renderForBeforeInput(label, { required, invalid, htmlFor: name });
      }

      return null;
    })();

    const suffix = (() => {
      if (errors.some) {
        return PropError.renderGroupAfterInput(errors);
      }

      if (note) {
        return PropNote.renderForAfterInput(note);
      }

      return null;
    })();

    return (
      <div>
        {prefix}
        <LooseTextInput
          {...{
            ...more,
            ...rest,
            ...propsForInput,
            type,
            name,
            id: name,
            required,
            disabled,
            /**
             * Technically, the ref can be either HTMLTextAreaElement or HTMLInputElement, which type for at the start of this function,
             * so rather than trying to figure out how to type this correctly, we'll just cheat a little and cast it. For the sake of the
             * interface of LooseTextInput, this is the right call.
             */
            ref: forkedRef(forwardedRef, ref) as (ref: HTMLInputElement) => void,
          }}
        />
        {suffix}
      </div>
    );
  };
};

export type BaseProps = Omit<LooseTextInputProps, InternalKeys | StandardOmittedKeys> &
  SharedPublicProps & {
    value: string;
  };

export const Base = React.forwardRef<HTMLInputElement, BaseProps>(inputFactory("text", {}));
Base.displayName = "TextInput.Base";

export const Code = React.forwardRef<HTMLInputElement, BaseProps>(inputFactory("text", { mono: true }));
Code.displayName = "TextInput.Code";

export const Password = React.forwardRef<HTMLInputElement, BaseProps>(inputFactory("password", { UNSAFE_className: "fs-exclude" }));
Password.displayName = "TextInput.Password";

export const Tel = React.forwardRef<HTMLInputElement, BaseProps>(inputFactory("tel", {}));
Tel.displayName = "TextInput.Tel";

export const Url = React.forwardRef<HTMLInputElement, BaseProps>(inputFactory("url", {}));
Url.displayName = "TextInput.Url";

export type SearchProps = BaseProps & {
  loading?: boolean;
};

const innerSearchFn = inputFactory("search", {});

export const Search = React.forwardRef<HTMLInputElement, SearchProps>((props, forwardedRef) => {
  const rightSide = props.loading ? <LoadingState /> : props.rightSide;
  return innerSearchFn({ ...props, rightSide }, forwardedRef);
});
Search.displayName = "TextInput.Search";

export type EmailProps = Omit<LooseTextInputProps, InternalKeys | EmailOmittedKeys> &
  SharedPublicProps & {
    value: string;
  };

export const Email = React.forwardRef<HTMLInputElement, EmailProps>(inputFactory("email", {}));
Email.displayName = "TextInput.Email";

export type NumberProps = Omit<LooseTextInputProps, InternalKeys | NumberOmittedKeys> &
  SharedPublicProps & {
    value: string | number;
  };

export const Number = React.forwardRef<HTMLInputElement, NumberProps>(inputFactory("number", {}));
Number.displayName = "TextInput.Number";

export type TextAreaProps = BaseProps & {
  mono?: boolean;
  rows?: number;
  autoResize?: boolean;
};

const innerTextAreaFn = inputFactory("textarea", { autoComplete: "off" });

export const TextArea = React.forwardRef<HTMLTextAreaElement, TextAreaProps>((props, forwardedRef) => {
  const rows = React.useMemo<number | undefined>(() => {
    const { rows = 5, autoResize, value } = props;

    if (autoResize) {
      return Math.max(value.split("\n").length, rows, 1);
    } else {
      return Math.max(rows, 1);
    }
  }, [props.rows, props.autoResize, props.value]);

  return innerTextAreaFn({ ...props, rows }, forwardedRef);
});
TextArea.displayName = "TextInput.TextArea";
